/**
 * Екран „Качване“ (Раздел 4.3 и 8).
 *
 * Всеки файл се качва поотделно със собствен прогрес и собствен резултат.
 * Отхвърлените се виждат в отделен списък с причината на български.
 */

import { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Group,
  Progress,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { getAccessToken } from '../api/client';

type ItemState = 'uploading' | 'accepted' | 'rejected' | 'error';

interface UploadItem {
  id: string;
  filename: string;
  state: ItemState;
  progress: number;
  message?: string;
  reason?: string;
}

const FILENAME_EXAMPLE = 'in-23-0884636615-20260901-103830.mp3';

/** XMLHttpRequest вместо fetch — нужен е прогрес на всеки файл. */
function uploadFile(
  file: File,
  onProgress: (percent: number) => void,
): Promise<{ status: number; body: unknown }> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const form = new FormData();
    form.append('file', file, file.name);

    request.open('POST', '/api/recordings/upload');
    const token = getAccessToken();
    if (token) request.setRequestHeader('Authorization', `Bearer ${token}`);
    request.withCredentials = true;

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });

    request.addEventListener('load', () => {
      let body: unknown = null;
      try {
        body = JSON.parse(request.responseText);
      } catch {
        body = null;
      }
      resolve({ status: request.status, body });
    });

    request.addEventListener('error', () => reject(new Error('Връзката прекъсна')));
    request.send(form);
  });
}

export function UploadPage() {
  const [items, setItems] = useState<UploadItem[]>([]);
  const counter = useRef(0);

  const update = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  }, []);

  const handleDrop = useCallback(
    (files: File[]) => {
      const queued = files.map((file) => ({
        id: `${Date.now()}-${(counter.current += 1)}`,
        filename: file.name,
        state: 'uploading' as ItemState,
        progress: 0,
      }));

      setItems((current) => [...queued, ...current]);

      files.forEach((file, index) => {
        const item = queued[index]!;

        void uploadFile(file, (percent) => update(item.id, { progress: percent }))
          .then(({ status, body }) => {
            const payload = body as { message?: string; reason?: string } | null;

            if (status === 201) {
              update(item.id, { state: 'accepted', progress: 100 });
            } else if (status === 422) {
              update(item.id, {
                state: 'rejected',
                progress: 100,
                reason: payload?.reason,
                message: payload?.message ?? 'Файлът е отхвърлен',
              });
            } else {
              update(item.id, {
                state: 'error',
                progress: 100,
                message: payload?.message ?? `Грешка ${status}`,
              });
            }
          })
          .catch((error: unknown) => {
            update(item.id, {
              state: 'error',
              message: error instanceof Error ? error.message : 'Неуспешно качване',
            });
          });
      });
    },
    [update],
  );

  const accepted = items.filter((item) => item.state === 'accepted');
  const rejected = items.filter((item) => item.state === 'rejected' || item.state === 'error');
  const inFlight = items.filter((item) => item.state === 'uploading');

  return (
    <Stack>
      <Title order={3}>Качване на записи</Title>

      <Alert color="blue" title="Формат на имената">
        <Text size="sm">
          Поддържа се един формат:{' '}
          <Code>{'{in|out}-{вътрешен_номер}-{телефон}-{ГГГГММДД}-{ЧЧММСС}.{mp3|wav}'}</Code>
        </Text>
        <Text size="sm" mt={4}>
          Пример: <Code>{FILENAME_EXAMPLE}</Code>. Файловете се изнасят от централата —
          ръчно преименуване не се допуска.
        </Text>
      </Alert>

      <Dropzone onDrop={handleDrop} accept={['audio/mpeg', 'audio/wav', 'audio/x-wav']} multiple>
        <Box py="xl" ta="center">
          <Text size="lg">Пуснете файловете тук или щракнете, за да изберете</Text>
          <Text size="sm" c="dimmed" mt={4}>
            MP3 или WAV, до 200 MB на файл
          </Text>
        </Box>
      </Dropzone>

      {items.length > 0 && (
        <Group>
          <Badge color="gray">Общо: {items.length}</Badge>
          <Badge color="blue">Качват се: {inFlight.length}</Badge>
          <Badge color="green">Приети: {accepted.length}</Badge>
          <Badge color="red">Отхвърлени: {rejected.length}</Badge>
          <Button variant="subtle" size="xs" onClick={() => setItems([])}>
            Изчисти списъка
          </Button>
        </Group>
      )}

      {items.length > 0 && (
        <Card withBorder>
          <Title order={5} mb="sm">
            Текуща партида
          </Title>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Файл</Table.Th>
                <Table.Th w={180}>Прогрес</Table.Th>
                <Table.Th w={120}>Състояние</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{item.filename}</Table.Td>
                  <Table.Td>
                    <Progress
                      value={item.progress}
                      color={item.state === 'uploading' ? 'blue' : 'gray'}
                    />
                  </Table.Td>
                  <Table.Td>
                    <StateBadge state={item.state} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {rejected.length > 0 && (
        <Card withBorder>
          <Title order={5} mb="sm">
            Отхвърлени файлове
          </Title>
          <Text size="sm" c="dimmed" mb="sm">
            Тези файлове не влизат в обработка. Коригирайте ги и ги качете отново.
          </Text>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Файл</Table.Th>
                <Table.Th>Причина</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rejected.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{item.filename}</Table.Td>
                  <Table.Td>
                    <Text c="red">{item.message}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Stack>
  );
}

function StateBadge({ state }: { state: ItemState }) {
  if (state === 'uploading') return <Badge color="blue">Качва се</Badge>;
  if (state === 'accepted') return <Badge color="green">Приет</Badge>;
  if (state === 'rejected') return <Badge color="red">Отхвърлен</Badge>;
  return <Badge color="orange">Грешка</Badge>;
}
